import { Injectable } from '@angular/core';
import { ConfigurationService, NgxToastrService, ExecutePortfolioRequest, StrategiesService, ConfirmationDialogService, PortfolioService, CARRYFORWARD_PORTFOLIOS_BROKER_WISE, RegulatoryData, REGULATORY_DISCLAIMER_TEXT } from '@app/_shared';
import { BILLING_PORTAL_ERROR, ALGO_SPACE_MAPPING_REQUIRED, ACTIVE_ALGO_LIMIT_REACHED, ALGO_SPACE_VM_DOWN, ALGO_SPACE_ONLY_ACTIVE_MAILABLE, ALGO_SPACE_EXPIRED, ALGO_SPACE_PROVISIONING, ALGO_SPACE_REMOVAL_BLOCKED_RUNNING_ALGOS, ALGO_SPACE_REQUIRED_FOR_LOGIN, ALGO_SPACE_MAPPING_FROZEN_WHILE_LOGGED_IN, ALGO_SPACE_MAX_MAPPING_REACHED, LOGIN_ALGO_SPACE_SELECTION_REQUIRED } from '@app/_shared/strings';
import { HttpClient } from '@angular/common/http';
import { BrokersResponse, AvailableBrokersResponse, ConnectedBrokersResponse, BrokerApiKeyResponse, AllBrokerMarginsResponse, BrokerMarginResponse, BrokerMarginBlockedResponse, BrokerPeakMarginResponse, AlgoSpaceResponse, MapAlgoSpaceResponse, ConnectedBroker, AlgoSpace, AlgoSpaceMapping, SpaceMappingResult, MapAlgoSpaceDialogData } from './manage-broker.interface';
import { tap, shareReplay, switchMap, catchError, finalize, map } from 'rxjs/operators';
import { EMPTY, forkJoin, from, of, Observable, throwError } from 'rxjs';
import { BrokerModel } from './manage-broker.model';
import { BrokerConnectionStatus, ProductType } from '../../../../../../src/utProto/enums_pb';
import { BriefPortfolioRequest } from '../market-place/providers/market-place/market-place.interface';
import { MyStrategyType } from '../my-strategies/my-strategies.consts';
import { WebApiTransactionType } from '../../../../../../src/utProto/webApiStrategyEnums_pb';
import { EXCHANGE_STRINGS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { CARRYFORWARD_PORTFOLIOS_BROKER_WISE_CACHE_KEY, DEFAULT_VIRTUAL_ALGO_SPACE_CONFIG, ECHART_DATE_FORMAT, MOMENT_TIME_FORMAT, Utils } from '@app/_helpers';
import * as moment from 'moment';

import { MarketPlaceService } from '../market-place/providers/market-place/market-place.service';
import { UserManagementService } from '@app/modules/admin/providers/user-management/user-management.service';
import { RegulatoryGuardService } from '@app/_shared/modules/regulatory-dialog/regulatory-guard.service';
import { AlgoSpaceOnBrokerSetupConfig, VirtualAlgoSpaceConfig } from '@app/_shared/providers/configuration/configuration.interfaces';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MbPlansService } from '../mb-plans/providers/mb-plans.service';
import { BILLING_PORTAL_ROUTES } from '../mb-plans/providers/mb-plans.constants';
import { ManageBrokersTab } from './manage-broker.enums';
import { MapAlgoSpaceComponent } from './map-algo-space/map-algo-space.component';
import { StrategyState } from '../operator-dashboard/strategies/strategies.enums';
import { AlgowisePositionDataService } from '@app/_shared/providers/algowise-position-data/algowise-position-data.service';

@Injectable({
  providedIn: 'root'
})
export class ManageBrokerService {

  cachedAvailableBrokers: AvailableBrokersResponse;
  cachedVendors: BrokersResponse;
  cachedConnectedBrokers: ConnectedBrokersResponse | null = null;
  private brokersRequest$: Observable<BrokersResponse> | null = null;
  private connectedBrokersRequest$: Observable<ConnectedBrokersResponse> | null = null;
  lastUpdatedAt: number;
  setupFormOpened: boolean = false;
  allowedBrokers: { [key: string]: boolean; };
  availableBrokersRequest$: Observable<AvailableBrokersResponse> | null = null;
  brokerUnderRegulation: Set<string> = new Set();
  /** Broker names where broker_available is false — hidden from Available tab, shown as "Not Available" elsewhere. */
  notAvailableBrokers: Set<string> = new Set();
  /** Broker names where trading_paused is true — trading disabled but broker may still be connected. */
  pausedBrokers: Set<string>  = new Set();

  private buyAlgoSpaceRedirectInFlight = false;
  virtualAlgoSpaceConfig: VirtualAlgoSpaceConfig;

  constructor(
    private confService: ConfigurationService,
    private http: HttpClient,
    private marketService: MarketPlaceService,
    private strategyService: StrategiesService,
    private confirmationService: ConfirmationDialogService,
    private portfolioService: PortfolioService,
    private toastr: NgxToastrService,
    private userManagementService: UserManagementService,
    private regulatoryGuardService: RegulatoryGuardService,
    private mbPlansService: MbPlansService,
    private algoWisePositionDataService: AlgowisePositionDataService,
  ) {
    this.allowedBrokers = this.confService.remoteConfigCacheObj?.BROKER_AVAILABLE;
    this.virtualAlgoSpaceConfig = this.confService.remoteConfigCacheObj?.VIRTUAL_ALGO_SPACE ?? DEFAULT_VIRTUAL_ALGO_SPACE_CONFIG;
  }

  getBrokers() {
    if (this.cachedVendors) {
      return of(this.cachedVendors);
    }
    if (!this.brokersRequest$) {
      // One API call shared by all callers. shareReplay(1) replays the result to late subscribers (e.g. same observable subscribed again), where cache is not used.
      this.brokersRequest$ = this.http.get<BrokersResponse>(this.confService.applicationUrls.getBrokers).pipe(
        tap(response => this.cachedVendors = response),
        shareReplay(1)
      );
    }
    return this.brokersRequest$;
  }

  getAvailableBrokers() {

    if(this.availableBrokersRequest$) {
      return this.availableBrokersRequest$;
    }

    if (this.cachedAvailableBrokers) {
      return of(this.cachedAvailableBrokers);
    }
    this.availableBrokersRequest$ = this.http.get<AvailableBrokersResponse>(this.confService.applicationUrls.getAvailableBrokers).pipe(
      tap(response => {
        this.availableBrokersRequest$ = null;
        this.cachedAvailableBrokers = response;
        this.notAvailableBrokers.clear();
        this.brokerUnderRegulation.clear();
        this.pausedBrokers.clear();
        // Classify each broker once; not_available takes precedence over regulation and paused states.
        for(const broker in response) {
          if(!response[broker].broker_available) {
            this.notAvailableBrokers.add(broker);
          } else if (response[broker].is_regulation_enabled) {
            this.brokerUnderRegulation.add(broker);
          } else if(response[broker].trading_paused) {
            this.pausedBrokers.add(broker);
          }
        }
      }),
      shareReplay({bufferSize: 1, refCount: true})
    );

    return this.availableBrokersRequest$;
  }

  getConnectedBrokers(fetchLatest: boolean = false) {
    if (fetchLatest) {
      this.connectedBrokersRequest$ = null;
    }
    if (!fetchLatest && this.cachedConnectedBrokers) {
      return of(this.cachedConnectedBrokers);
    }
    if (!fetchLatest && this.connectedBrokersRequest$) {
      return this.connectedBrokersRequest$;
    }
    this.connectedBrokersRequest$ = this.http.get<ConnectedBrokersResponse>(this.confService.applicationUrls.getConnectedBrokers).pipe(
      tap(response => {
        if (response?.data?.length > 0) {
          this.cachedConnectedBrokers = response;
          this.notAvailableBrokers.clear();
          this.pausedBrokers.clear();
          for(const broker of response.data) {
            if(!broker.broker_available ) {
              this.notAvailableBrokers.add(broker.broker);
            } else if(broker.trading_paused ) {
              this.pausedBrokers.add(broker.broker);
            }
          }
        }
      }),
      shareReplay(1)
    );
    return this.connectedBrokersRequest$;
  }

  getDeployDialogBrokerData(): Observable<{
    connectedBrokers: ConnectedBroker[];
    brokersData: BrokersResponse['login_flows'];
    availableBrokers: AvailableBrokersResponse;
  }> {
    return forkJoin({
      connectedBrokers: this.getConnectedBrokers(true),
      algoSpaces: this.getAlgoSpaces({ withDeployments: true }),
      brokers: this.getBrokers(),
      availableBrokers: this.getAvailableBrokers(),
    }).pipe(
      map(({ connectedBrokers, algoSpaces, brokers, availableBrokers }) => ({
        connectedBrokers: this.enrichBrokersWithAlgoSpaceDeployments(
          connectedBrokers.data ?? [],
          algoSpaces.data ?? [],
        ),
        brokersData: brokers.login_flows,
        availableBrokers,
      })),
    );
  }

  getConnectedBrokerByConnectionId(connectionId: number): ConnectedBroker | undefined {
    return this.cachedConnectedBrokers?.data?.find((broker) => broker.id === connectionId);
  }

  mergeConnectedBrokerUpdate(existing: ConnectedBroker | undefined, update: Partial<ConnectedBroker>): ConnectedBroker {
    if (!existing) {
      return update as ConnectedBroker;
    }
    return {
      ...existing,
      ...update,
      algo_spaces: update.algo_spaces !== undefined ? update.algo_spaces : existing.algo_spaces,
    };
  }

  getBrokerAndCallCarryForward(connectedBrokers: ConnectedBrokersResponse, connectedBrokerId: string) {
    const broker = connectedBrokers.data?.find(broker => broker.broker_id === connectedBrokerId);
    if (broker) {
      this.carryForwardToResume(broker.broker);
    }
  }

  getBrokerApiKey(broker: string, brokerId: string) {
    return this.http.get<BrokerApiKeyResponse>(this.confService.applicationUrls.getConnectedBrokers + broker + '/credentials/' + brokerId + '/');
  }

  renameConnection(broker: string, connectionId: number, connectionName: string, broker_id: string) {
    return this.http.put<any>(this.confService.applicationUrls.getConnectedBrokers + broker + '/' + connectionId + '/', { broker_id: broker_id, connection_name: connectionName });
  }

  deleteConnection(broker: string, broker_id: string) {
    return this.http.delete<any>(this.confService.applicationUrls.getConnectedBrokers + broker + '/' + broker_id + '/');
  }

  generateRedirectURL(broker: string, broker_id: string) {
    return this.http.post<any>(this.confService.applicationUrls.getConnectedBrokers + 'redirect-url/', {broker: broker, broker_id: broker_id});
  }

  saveBroker(broker: string, brokerModel: BrokerModel) {
    return this.http.post<any>(this.confService.applicationUrls.getConnectedBrokers + broker, brokerModel);
  }

  loginBroker(broker: string, broker_id: string, space_id?: string) {
    const params = space_id ? { space_id } : undefined;
    return this.http.get<any>(
      this.confService.applicationUrls.loginBroker + broker + '/dashboard/init/' + broker_id,
      { params },
    );
  }

  sendRedirectToken(broker: string, broker_id: string, params: any) {
    return this.http.post<any>(this.confService.applicationUrls.loginBroker + broker + '/dashboard/callback/' + broker_id + '/', params);
  }

  getBrokerMargin(broker: string, broker_id: string) {
    return this.http.get<BrokerMarginResponse>(this.confService.applicationUrls.getConnectedBrokers + broker + '/margins/' + broker_id);
  }

  getAllBrokerMargins() {
    return this.http.get<AllBrokerMarginsResponse>(this.confService.applicationUrls.getConnectedBrokers + 'all/margins/').pipe(
      tap(response => this.lastUpdatedAt = Date.now())
    );
  }

  logoutBroker(broker: string, broker_id: string) {
    return this.http.delete<any>(this.confService.applicationUrls.getConnectedBrokers + 'sessions/' + broker + '/' + broker_id + '/');
  }

  directLogin(broker: string, broker_id: string, credentials: any = null, space_id?: string) {
    const body: Record<string, unknown> = { broker_id };
    if (space_id) {
      body.space_id = space_id;
    }
    if (credentials) {
      body.runtime_credentials = credentials;
      body.credentials = credentials;
    }
    return this.http.post<any>(this.confService.applicationUrls.directLogin + broker + '/', body).pipe(
      tap((response: any) => {
        if (response.success) {
          this.carryForwardToResume(broker);
        }
      })
    );
  }

  checkSessionStatus(broker: string, broker_id: string) {
    return this.http.get<any>(this.confService.applicationUrls.getConnectedBrokers + 'sessions/' + broker + '/' + broker_id + '/status/');
  }

  getAlgoSpaces(options: { withDeployments?: boolean; brokerConnectionId?: number } = {}): Observable<AlgoSpaceResponse> {
    const params: Record<string, string> = {};
    if (options.withDeployments) {
      params.algo_deployments = 'true';
    }
    if (options.brokerConnectionId != null) {
      params.broker_connection_id = String(options.brokerConnectionId);
    }
    const hasParams = Object.keys(params).length > 0;
    return this.http.get<AlgoSpaceResponse>(this.confService.applicationUrls.getAlgoSpaces, {
      params: hasParams ? params : undefined,
    });
  }

  /**
   * Algo space assignment — FE/BE responsibility split (grooming 15-06-2026, UT-9061):
   *
   * BE (`POST …/profile/virtual-spaces/brokers/{connection_id}`) enforces removal when logged-in + running algos.
   *
   * FE pre-validates where fresh data is available:
   * - Map: only `active` spaces can be added; `expired` / `provisioning` are disabled.
   * - Map: block unmap when broker is logged in and deployed algos are running (deployed list).
   * - Deploy: mapping required, VM down, capacity, expired/provisioning space checks.
   * - Map dialog refresh reloads space list and running-algo state.
   * BE remains authoritative on submit failures.
   */
  mapAlgoSpace(connectionId: number, spaces: AlgoSpaceMapping[]): Observable<MapAlgoSpaceResponse> {
    return this.http.post<MapAlgoSpaceResponse>(
      `${this.confService.applicationUrls.mapAlgoSpace}${connectionId}`,
      { spaces },
    );
  }

  applyMapAlgoSpaceResults(broker: ConnectedBroker, results: SpaceMappingResult[], availableSpaces: AlgoSpace[]): ConnectedBroker {
    const algo_spaces = [...(broker.algo_spaces ?? [])];
    results.filter((result) => result.success).forEach((result) => {
      if (result.mapped) {
        const space = availableSpaces.find((item) => item.id === result.space_id);
        if (space && !algo_spaces.some((item) => item.id === space.id)) {
          algo_spaces.push(space);
        }
      } else {
        const index = algo_spaces.findIndex((item) => item.id === result.space_id);
        if (index !== -1) {
          algo_spaces.splice(index, 1);
        }
      }
    });
    return { ...broker, algo_spaces };
  }

  openMapAlgoSpaceDialog(
    dialog: MatDialog,
    broker: ConnectedBroker,
    from: string,
    onUpdate?: (broker: ConnectedBroker) => void,
  ): MatDialogRef<MapAlgoSpaceComponent> {
    const cached = this.getConnectedBrokerByConnectionId(broker.id);
    const brokerDetails = cached ? this.mergeConnectedBrokerUpdate(cached, broker) : broker;
    const ref = dialog.open(MapAlgoSpaceComponent, {
      width: '490px',
      panelClass: ['broker-selection-dialog'],
      autoFocus: false,
      restoreFocus: false,
      data: { brokerDetails, from } satisfies MapAlgoSpaceDialogData,
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated?.broker_id) {
        this.getConnectedBrokers(true).subscribe((response) => {
          const freshBroker = response.data?.find((item) =>
            item.id === updated.id || (item.broker === updated.broker && item.broker_id === updated.broker_id),
          );
          onUpdate?.(freshBroker ?? updated);
        });
      }
    });
    return ref;
  }

  openAlgoSpaceSelectDialog(
    dialog: MatDialog,
    from: string,
    options: {
      brokerName: string;
      brokerDisplayName?: string;
      brokerImageLink?: string;
      initialSelectedSpaceIds?: string[];
      mandatory?: boolean;
    },
  ): MatDialogRef<MapAlgoSpaceComponent> {
    return dialog.open(MapAlgoSpaceComponent, {
      width: '490px',
      panelClass: ['broker-selection-dialog'],
      autoFocus: false,
      restoreFocus: false,
      data: {
        from,
        selectOnly: true,
        mandatory: options.mandatory,
        brokerName: options.brokerName,
        brokerDisplayName: options.brokerDisplayName,
        brokerImageLink: options.brokerImageLink,
        initialSelectedSpaceIds: options.initialSelectedSpaceIds,
      } satisfies MapAlgoSpaceDialogData,
    });
  }

  private getAlgoSpaceOnBrokerSetupConfig(brokerName: string): AlgoSpaceOnBrokerSetupConfig | undefined {
    return this.confService.remoteConfigCacheObj?.MULTI_BROKER_DETAILS?.[brokerName]
      ?.algo_space_on_broker_setup as AlgoSpaceOnBrokerSetupConfig | undefined;
  }

  /** Setup popup: show algo space section when Firebase `algo_space_on_broker_setup.enabled` is true. */
  isAlgoSpaceOnBrokerSetupEnabled(brokerName: string): boolean {
    if (!this.virtualAlgoSpaceConfig.is_enabled) {
      return false;
    }
    return this.getAlgoSpaceOnBrokerSetupConfig(brokerName)?.enabled === true;
  }

  /** Setup popup: block broker setup without mapped space when Firebase `is_mandatory` is true. */
  isAlgoSpaceMappingMandatory(brokerName: string): boolean {
    return this.getAlgoSpaceOnBrokerSetupConfig(brokerName)?.is_mandatory === true;
  }

  /** Trading/deploy requires a mapped virtual space (`algo_space_required` from broker schema). */
  isAlgoSpaceRequiredForBroker(brokerName: string): boolean {
    if (!this.virtualAlgoSpaceConfig.is_enabled) {
      return false;
    }
    return this.cachedAvailableBrokers?.[brokerName]?.algo_space_required ?? false;
  }

  /** Login APIs must receive `space_id` (`login_space_required` from broker schema). */
  isLoginSpaceRequiredForBroker(brokerName: string): boolean {
    if (!this.virtualAlgoSpaceConfig.is_enabled) {
      return false;
    }
    return !!this.cachedAvailableBrokers?.[brokerName]?.login_space_required;
  }

  showsAlgoSpaceOnBrokerCard(brokerName: string): boolean {
    return this.isLoginSpaceRequiredForBroker(brokerName) || this.isAlgoSpaceRequiredForBroker(brokerName);
  }

  getMaxAllowedMapping(brokerName: string): number | null {
    const brokerSchema = this.cachedAvailableBrokers?.[brokerName];
    if (brokerSchema?.max_space_mappings !== undefined) {
      return brokerSchema.max_space_mappings;
    }
    if (brokerSchema?.max_broker_mappings !== undefined) {
      return brokerSchema.max_broker_mappings;
    }
    return null;
  }

  resolveLoginSpaceId(broker: ConnectedBroker, preferredSpaceId?: string | null): string | null {
    const mappedIds = broker.algo_spaces?.map((space) => space.id) ?? [];
    if (!mappedIds.length) {
      return null;
    }
    if (preferredSpaceId && mappedIds.includes(preferredSpaceId)) {
      return preferredSpaceId;
    }
    if (mappedIds.length === 1) {
      return mappedIds[0];
    }
    return null;
  }

  getLoginAlgoSpaceBlockReason(broker: ConnectedBroker, preferredSpaceId?: string | null): string | null {
    if (!this.isLoginSpaceRequiredForBroker(broker.broker)) {
      return null;
    }
    if (!this.hasMappedAlgoSpace(broker)) {
      return ALGO_SPACE_REQUIRED_FOR_LOGIN;
    }
    if (!this.resolveLoginSpaceId(broker, preferredSpaceId)) {
      return LOGIN_ALGO_SPACE_SELECTION_REQUIRED;
    }
    return null;
  }

  isAlgoSpaceMappingFrozen(broker: ConnectedBroker): boolean {
    return this.isLoginSpaceRequiredForBroker(broker.broker) && this.isBrokerLoggedIn(broker);
  }

  initOAuthLogin(broker: ConnectedBroker, preferredSpaceId?: string | null) {
    const loginBlock = this.getLoginAlgoSpaceBlockReason(broker, preferredSpaceId);
    if (loginBlock) {
      return throwError(() => ({ error: { message: loginBlock } }));
    }
    const spaceId = this.resolveLoginSpaceId(broker, preferredSpaceId);
    return this.loginBroker(broker.broker, broker.broker_id, spaceId ?? undefined);
  }

  directLoginBroker(broker: ConnectedBroker, credentials: any = null, preferredSpaceId?: string | null) {
    const loginBlock = this.getLoginAlgoSpaceBlockReason(broker, preferredSpaceId);
    if (loginBlock) {
      return throwError(() => ({ error: { message: loginBlock } }));
    }
    const spaceId = this.resolveLoginSpaceId(broker, preferredSpaceId);
    return this.directLogin(broker.broker, broker.broker_id, credentials, spaceId ?? undefined);
  }

  buildCredentialSpacesPayload(spaceIds: string[]): AlgoSpaceMapping[] {
    return spaceIds.map((space_id) => ({ space_id, mapped: true }));
  }

  isBrokerLoggedIn(broker: ConnectedBroker): boolean {
    return broker?.status === BrokerConnectionStatus.CONNECTIONSTATUS_ACTIVE;
  }

  // Fetch All / unpaginated deployed portfolios and check if any of them are running on the broker.
  hasRunningAlgosOnBroker(brokerName: string): Observable<boolean> {
    const request: BriefPortfolioRequest = { page_number: 0, page_size: 0 };
    return this.marketService.getPortfoliosByType(MyStrategyType.Deployed, request).pipe(
      map((response) => response.data.some((portfolio) =>
       {
        portfolio.positionObjects = this.algoWisePositionDataService.getPortfolioLevelPositions(portfolio.uniquePortfolioID);
        return portfolio.broker === brokerName && (
          (portfolio.deployed ||
          portfolio.paused ||
          (portfolio.terminated && portfolio.isPositionAvailable)
          ))
       }
      )),
      catchError(() => of(false))
    );
  }

  private enrichBrokersWithAlgoSpaceDeployments(
    brokers: ConnectedBroker[],
    algoSpaces: AlgoSpace[],
  ): ConnectedBroker[] {
    if (!algoSpaces.length) {
      return brokers;
    }
    const spaceById = new Map(algoSpaces.map((space) => [space.id, space]));
    return brokers.map((broker) => ({
      ...broker,
      algo_spaces: broker.algo_spaces?.map((space) => {
        const fresh = spaceById.get(space.id);
        return fresh ? { ...space, ...fresh } : space;
      }),
    }));
  }

  /** Status-only block check (expired/provisioning/non-active); independent of any broker connection context. */
  getAlgoSpaceStatusBlockReason(space: AlgoSpace, wantsMapped: boolean): string | null {
    if (!wantsMapped) {
      return null;
    }
    if (space?.status === 'expired') {
      return ALGO_SPACE_EXPIRED;
    }
    if (space?.status === 'provisioning') {
      return ALGO_SPACE_PROVISIONING;
    }
    if (space?.status && space.status !== 'active') {
      return ALGO_SPACE_ONLY_ACTIVE_MAILABLE;
    }
    return null;
  }

  getAlgoSpaceMapBlockReason(
    space: AlgoSpace,
    broker: ConnectedBroker,
    wantsMapped: boolean,
    brokerHasRunningAlgos: boolean,
  ): string | null {
    if (wantsMapped) {
      const statusBlockReason = this.getAlgoSpaceStatusBlockReason(space, wantsMapped);
      if (statusBlockReason) {
        return statusBlockReason;
      }
      const alreadyMappedToBroker = broker.algo_spaces?.some((item) => item.id === space.id);
      if (!alreadyMappedToBroker && space.max_broker_mappings != null
        && (space.mapped_broker_count ?? 0) >= space.max_broker_mappings) {
        return ALGO_SPACE_MAX_MAPPING_REACHED;
      }
      if (this.isLoginSpaceRequiredForBroker(broker.broker) && this.isBrokerLoggedIn(broker)) {
        const alreadyMapped = broker.algo_spaces?.some((item) => item.id === space.id);
        if (!alreadyMapped) {
          return ALGO_SPACE_MAPPING_FROZEN_WHILE_LOGGED_IN;
        }
      }
      return null;
    }
    if (this.isLoginSpaceRequiredForBroker(broker.broker) && this.isBrokerLoggedIn(broker)) {
      return ALGO_SPACE_MAPPING_FROZEN_WHILE_LOGGED_IN;
    }
    if (this.isBrokerLoggedIn(broker) && brokerHasRunningAlgos
      && broker.algo_spaces?.some((item) => item.id === space.id)) {
      return ALGO_SPACE_REMOVAL_BLOCKED_RUNNING_ALGOS;
    }
    return null;
  }

  getAlgoSpaceMaxMappingBlockReason(brokerName: string, selectedCount: number, wantsMapped: boolean): string | null {
    if (!wantsMapped) {
      return null;
    }
    const max = this.getMaxAllowedMapping(brokerName);
    if (max !== null && selectedCount >= max) {
      return ALGO_SPACE_MAX_MAPPING_REACHED;
    }
    return null;
  }

  hasMappedAlgoSpace(broker: ConnectedBroker): boolean {
    return !!broker?.algo_spaces?.length;
  }

  isAlgoSpaceDeployable(space: AlgoSpace): boolean {
    const used = space.deployed_algo_count ?? 0;
    const max = space.max_deployed_count;
    const belowCapactiy = max === null || used < max;
    return (!space.status || space.status === 'active') && !space.vm_down && belowCapactiy;
  }

  getAlgoSpaceDeploymentLabel(space: AlgoSpace): string {
    const used = space.deployed_algo_count ?? 0;
    const max = space.max_deployed_count ?? 0;
    return `${used}/${max}`;
  }

  getSelectedAlgoSpace(broker: ConnectedBroker | null, spaceId: string | null): AlgoSpace | null {
    return broker?.algo_spaces?.find((space) => space.id === spaceId) ?? broker?.algo_spaces?.[0] ?? null;
  }

  getAlgoSpaceDeployError(broker: ConnectedBroker | null, space: AlgoSpace | null, isForward: boolean): string | null {
    if (isForward || !broker || !this.isAlgoSpaceRequiredForBroker(broker.broker)) {
      return null;
    }
    if (!this.hasMappedAlgoSpace(broker) || !space) {
      return ALGO_SPACE_MAPPING_REQUIRED;
    }
    if (space.status === 'expired') {
      return ALGO_SPACE_EXPIRED;
    }
    if (space.status === 'provisioning') {
      return ALGO_SPACE_PROVISIONING;
    }
    if (space.vm_down) {
      return ALGO_SPACE_VM_DOWN;
    }
    if (!this.isAlgoSpaceDeployable(space)) {
      return ACTIVE_ALGO_LIMIT_REACHED;
    }
    return null;
  }

  getAssignAlgoSpaceUrl(broker_id: string): string {
    return `/trading/manage-brokers?selectedTab=${ManageBrokersTab.CONNECTED}&openMapForBroker=${broker_id}`;
  }

  redirectToBuyAlgoSpace(): void {
    if (this.buyAlgoSpaceRedirectInFlight) {
      return;
    }
    this.buyAlgoSpaceRedirectInFlight = true;
    this.mbPlansService.getSignedRedirectUrl(BILLING_PORTAL_ROUTES.AlgoSpace).pipe(
      finalize(() => { this.buyAlgoSpaceRedirectInFlight = false; }),
    ).subscribe({
      next: (res) => {
        if (res.success && res.data?.signed_url) {
          Utils.navigateToUrl(res.data.signed_url);
          return;
        }
        this.toastr.simpleCustomToaster(BILLING_PORTAL_ERROR, false);
      },
      error: () => {
        this.toastr.simpleCustomToaster(BILLING_PORTAL_ERROR, false);
      },
    });
  }

  getBrokerMarginBlocked(broker: string[] , client_id: string) {
    return this.http.get<BrokerMarginBlockedResponse>(this.confService.applicationUrls.getBrokerMarginBlocked, { params: { broker: broker, client_id: client_id } });
  }

  getBrokerPeakMargin(broker: string[], client_id: string) {
    return this.http.get<BrokerPeakMarginResponse>(this.confService.applicationUrls.getBrokerPeakMargin, { params: { broker: broker, client_id: client_id } });
  }

  checkBrokerConnectionStatus(brokerName: string) {
    if (brokerName === '' || brokerName === null || brokerName === undefined) {
      return true;
    }
    return this.cachedConnectedBrokers?.data?.find(broker => broker.broker === brokerName)?.status === BrokerConnectionStatus.CONNECTIONSTATUS_ACTIVE;

  }

  isBrokerConnectionAllowed(brokerName: string) {
    if (this.allowedBrokers === undefined || this.allowedBrokers === null) {
      return true;
    }
    return this.allowedBrokers?.[brokerName] ?? true;
  }
  
  carryForwardToResume(brokerConnected: string) {
    const todayKey = moment().format(ECHART_DATE_FORMAT);
    const carryForwardPortfolios: ExecutePortfolioRequest[] = [];

    this.userManagementService.getCache(CARRYFORWARD_PORTFOLIOS_BROKER_WISE_CACHE_KEY).pipe(
      switchMap((cacheRes) => {
        let data: Record<string, string>;
        try {
          data = cacheRes?.data ? JSON.parse(cacheRes.data) : {};
        } catch (error) {
          return throwError(() => error);
        }
        if (data?.[brokerConnected] === todayKey) {
          return EMPTY;
        }
        data[brokerConnected] = todayKey;
        let cachePayload = JSON.stringify(data);
        const request: BriefPortfolioRequest = {
          page_number: 0,
          page_size: 0,
        };
        const type = MyStrategyType.Deployed;
        return this.marketService.getPortfoliosByType(type, request).pipe(
          tap((portfolios) => {
            portfolios.data.forEach((portfolio) => {
              if (portfolio.productType[0].productType === ProductType.PRODUCTTYPE_CARRYFORWARD && portfolio.deployed && (portfolio.stopped || portfolio.paused)) {
                if (brokerConnected === portfolio.broker) {
                  const req: ExecutePortfolioRequest = {
                    unique_portfolio_id: portfolio.uniquePortfolioID,
                    portfolioID: portfolio.portfolioID,
                    state: (portfolio.paused || portfolio.stopped) ? WebApiTransactionType.WEBAPITRANSACTIONTYPE_RUN : WebApiTransactionType.WEBAPITRANSACTIONTYPE_PAUSE
                  };
                  carryForwardPortfolios.push(req);
                }
              }
            });
          }),
          switchMap(() => {
            if (carryForwardPortfolios.length === 0) {
              return this.userManagementService.setCache(CARRYFORWARD_PORTFOLIOS_BROKER_WISE_CACHE_KEY, cachePayload);
            }
            return from(Promise.resolve(this.confService.isTodayHoliday)).pipe(
              switchMap((isTodayHoliday: boolean) => {
                return this.strategyService.getTimeConf().pipe(tap((timeConf) => {
                  const confTime = timeConf.find(s => s.market == EXCHANGE_STRINGS[4]);
                  const CF_End_time = moment(confTime.carry_forward_end_time, MOMENT_TIME_FORMAT);
                  if ((!isTodayHoliday && moment().isBefore(CF_End_time))) {
                    this.resumePortfolios(carryForwardPortfolios, brokerConnected);
                  }
                }));
              }),
              switchMap(() => this.userManagementService.setCache(CARRYFORWARD_PORTFOLIOS_BROKER_WISE_CACHE_KEY, cachePayload))
            );
          })
        );
      }),
      catchError((err) => {
        console.error('carryForwardToResume failed:', err);
        return EMPTY;
      })
    ).subscribe();
  }


  async resumePortfolios(carryForwardPortfolios: ExecutePortfolioRequest[], broker: string) {
    const portfolioIds: string[] = carryForwardPortfolios.map(portfolio => portfolio.portfolioID);
    let regulatoryData: RegulatoryData[] = [];
    const isRegulationComplianceEnabled = this.confService.remoteConfigCacheObj?.IS_REGULATORY_COMPLIANCE_ENABLED;
    if(isRegulationComplianceEnabled){
      try {
        await this.getAvailableBrokers().toPromise();
        const regulatoryResponse = await this.portfolioService.checkPortfolioRegulation(portfolioIds).toPromise();
        regulatoryData = regulatoryResponse?.regulatory_data || [];
      }catch(err){
        console.error('Unable to check portfolio regulation:', err);
        return;
      }
    }
    let regulatoryConfirmation = false;
    let executeFunction = async () => {
      let isUnderRegulation = false;
      if (regulatoryData.length && this.cachedAvailableBrokers[broker]?.is_regulation_enabled) {
        isUnderRegulation = true;
        regulatoryConfirmation = await this.regulatoryGuardService.confirmIfRegulated(regulatoryData, REGULATORY_DISCLAIMER_TEXT(true));
        if (!regulatoryConfirmation) {
          return;
        }
      }
      if(isUnderRegulation) {
        carryForwardPortfolios.forEach(portfolio => {
          portfolio.regulatory_confirmation = !!regulatoryConfirmation;
        });
      }
      this.bulkRequest(carryForwardPortfolios);
    };
    
    const messageString = CARRYFORWARD_PORTFOLIOS_BROKER_WISE(carryForwardPortfolios.length);
    this.confirmationService.alert({
      message: messageString,
      buttons: [
        {
          text: 'No',
          accent: true,
          class: 'undeploy-button',
        },
        {
          text: 'Yes',
          accent: true,
          class: 'new-theme-gradient-bg',
          handler: executeFunction
        }
      ],
    }, {
      disableClose: false,
    });
  }

  bulkRequest(carryforwardPortfolios: ExecutePortfolioRequest[]) {
    this.portfolioService.bulkPortfoliosRequests(carryforwardPortfolios)
      .subscribe({
        next: res => {
        if (res && res['message']) {
          this.toastr.simpleCustomToaster(res['message']);
        };
      },
      error: err => {
        console.error('bulkRequest failed:', err);
        this.toastr.simpleCustomToaster('Failed to resume portfolios. Please try again later.', false);
      },
    })
  }

}
